import {
  AfterContentInit,
  Component,
  ElementRef,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewEncapsulation,
  inject,
  input,
  WritableSignal,
  signal,
} from '@angular/core';
import { ContactFormType } from '@app/src/app/classes/form/contact.form.type';
import {
  Alert,
  ContentFlex,
  FormButton,
  FormFooter,
  FormGroup,
  FormRow,
  FormSubmitEmitterType,
  HttpClientFormattedErrorType,
  Translation,
} from '@ckmk/angular';
import { ContactRequestMutation } from '@app/src/app/graphql/contact/contact.request.mutation';
import { NavigationStart, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';

@Component({
  selector: 'contact-form',
  standalone: true,
  imports: [
    FormButton,
    FormFooter,
    FormGroup,
    FormRow,
    Translation,
    Alert,
    ContentFlex,
  ],
  templateUrl: './contact-form.html',
  styleUrl: './contact-form.scss',
  encapsulation: ViewEncapsulation.None,
})
export class ContactForm
  implements OnInit, OnChanges, OnDestroy, AfterContentInit
{
  protected readonly self = inject<ElementRef<HTMLElement>>(ElementRef);

  protected readonly query = inject(ContactRequestMutation);

  protected readonly router = inject(Router);

  protected readonly formType = inject(ContactFormType);

  public readonly name = input<string>('contact_form');

  public readonly subject = input<string>();

  protected readonly localData: WritableSignal<any> = signal<any>({});

  protected readonly sent: WritableSignal<boolean> = signal<boolean>(false);

  protected readonly subscriptions: WritableSignal<Subscription[]> = signal<
    Subscription[]
  >([]);

  protected error: WritableSignal<HttpClientFormattedErrorType | null> =
    signal<HttpClientFormattedErrorType | null>(null);

  public constructor() {}

  public ngOnInit() {
    this.query.onQuery(
      (res, loading, callback) => {
        if (!loading && res) {
          if (res.sent) {
            this.sent.set(res.sent);
          } else if (callback) {
            callback();
          }
        }
      },
      (err, callback) => {
        if (err) {
          if (callback) {
            callback(() => {
              this.error.set(err);
            });
          } else {
            this.error.set(err);
          }
        }
      },
    );

    this.subscriptions.set([
      this.router.events
        .pipe(filter((event) => event instanceof NavigationStart))
        .subscribe({
          next: (event) => this.clearData(),
        }),
    ]);

    this.updateLocalData();
  }

  public ngOnChanges({ subject }: SimpleChanges) {
    if (subject && !subject.firstChange) {
      this.updateLocalData();
    }
  }

  public ngOnDestroy() {
    this.clearData();
  }

  public ngAfterContentInit() {}

  protected clearData(): void {
    this.query.unsubscribe(true);

    this.subscriptions.update((list) =>
      list.filter((sub) => sub.unsubscribe()),
    );

    this.sent.set(false);

    this.error.set(null);
  }

  protected updateLocalData(): void {
    const subject = this.subject();

    if (subject !== this.localData().subject) {
      this.localData.set({
        subject: subject,
      });
    }
  }

  protected onSubmit(event: FormSubmitEmitterType): void {
    const payload = event.data[event.name];

    this.query.mutate(
      {
        payload,
      },
      undefined,
      event.enable,
    );
  }
}
